import React, { useState, useEffect, useContext } from 'react';
import 'react-quill/dist/quill.snow.css'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from "../util/getConfigValue";
import AlertModal from '../components/AlertModal';
import { saveAnnoucement } from "../API/announcements";
import { v4 as uuid } from 'uuid';
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import TextEditor from '../components/TextEditor';
import PageTitle from '../components/PageTitle';
import { BrandsModal } from '../components/BrandSelectorModal';
import AnouncementsCard from "../components/leaderboard/AnnouncementsCard";
import { announcmentPartition } from '../util/constants';
import { ProductsModalButton } from '../components/ProductsModal';

export default function DashboardAnnouncementEditPage() {
	const [appState] = useContext(AppContext);
	const { state } = useLocation();
	const [currentAnnouncement] = useState(state?.announcement);
	const [previousState] = useState({ id: currentAnnouncement?.id, enabled: currentAnnouncement?.isActive, content: currentAnnouncement?.content, productId: currentAnnouncement?.productId})
	const [annoucementId, setAnnoucementId] = useState(state?.announcement?.id);
	const [content, setContent] = useState(state?.announcement?.content);
	const [annoucementsEnabled, setAnnoucementsEnabled] = useState(currentAnnouncement?.isActive || true);
	const [productId, setProductId] = useState(state?.announcement?.productId);
	const [hasPendingChange, setHasPendingChange] = useState(false);
	const [userRole] = useState(appState.role);

	const [alert, setAlert] = useState(state?.alert || null);
	const navigate = useNavigate();
	const maxAnnouncementCharacterCount = getConfigValue(appState?.config?.AnnouncementManagement?.MaxCharacterCount, appState)?.Value || 1700;
	const [rawCharacterCount, setRawCharacterCount] = useState(0);
	const { setBreadcrumbs } = useBreadcrumbs();
	const adminDashboardAnnouncementManagementEnabled = getConfigValue(appState?.config?.Admin?.DashboardAnnouncementManagement?.Enabled, appState)?.Value === 'true';
	const [showProductsModal, setShowProductsModal] = useState(false);
	const [currentProduct, setCurrentProduct] = useState(null);
	const products = [{ id: null, name: getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.AllBrands, appState)?.Value, image: null, clientProductId: null }, ...appState?.productDetails || []];
	const [productImageSource, setProductImageSource] = useState();

	useEffect(() => {
		const breadcrumbLabel = currentAnnouncement
			? getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.Edit?.Label, appState)?.Value
			: getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.Add?.Label, appState)?.Value;
		setBreadcrumbs([
			{ label: getConfigValue(appState?.config?.AnnouncementManagement?.Title, appState)?.Value || "Announcement Management", path: "/AnnouncementManagementPage" },
			{ label: breadcrumbLabel || "Announcement Banner", path: "" }
		]);
	}, [setBreadcrumbs, appState, currentAnnouncement]);

	useEffect(() => {	
		setContent(currentAnnouncement?.content);
		setAnnoucementsEnabled(currentAnnouncement?.isActive ?? true);
		setAnnoucementId(currentAnnouncement?.id);
		setProductId(currentAnnouncement?.productId);
		setCurrentProduct(products.find(p => p.id === (currentAnnouncement?.productId || null)));
		setProductImageSource(products.find(p => p.id === currentAnnouncement?.productId || null)?.imagePath);
	}, []);

	useEffect(() => {
		if (alert !== null) {
			setTimeout(() => {
				setAlert(null);
			}, 10000)
		}
	}, [alert]);

	useEffect(() => {
		if (!adminDashboardAnnouncementManagementEnabled || (userRole !== 'Admin' && userRole !== 'Precision')) {
			navigate('/');
		}
	}, [adminDashboardAnnouncementManagementEnabled, userRole]);

	function handleAnnoucementsEnableChecked(event) {
		setHasPendingChange(previousState.enabled !== event.target.checked);
		setAnnoucementsEnabled(event.target.checked);
	}

	const handleSaveAnnoucementClick = async (event) => {
		const formatedContent = formatContent(content);

		if (rawCharacterCount === 0) {
			setAlert({ type: 'danger', heading: (getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Error, appState)?.Value || null) });
			return;
		}

		const response = await saveAnnoucement(announcmentPartition, annoucementId || uuid(), annoucementsEnabled, formatedContent, productId);

		if (response.status === 200) {
			navigate('/AnnouncementManagementPage', { state: { alert: { type: 'success', heading: (getConfigValue(appState?.config?.AnnouncementManagement?.Success?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Success, appState)?.Value || null) } } });
		} else if (response.status === 409) {
			// show overwrite modal
			setAlert({ type: 'danger', heading: (getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Duplicate, appState)?.Value || "Duplicate Announcement") });
		} else {
			setAlert({ type: 'danger', heading: (getConfigValue(appState?.config?.AnnouncementManagement?.Failure?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.AnnouncementManagement?.Messages?.Save?.Error, appState)?.Value || null) });
		}
	}

	function formatContent(content) {
		content = content.replace(
			/rgb\((\d+),\s*(\d+),\s*(\d+)\)(?!\s*;)/g,
			'rgba($1, $2, $3, 1)');

		return content;
	}

    function onProductClick(product) {
		setHasPendingChange(previousState.productId !== product.id);
		setProductId(product.id);
		setCurrentProduct(product);
		setProductImageSource(products.find(p => p.id === product?.id || null)?.imagePath);
        setShowProductsModal(false);
    }

	function onOverwriteClick(product) {
		// find the annoucement
		const announcementToOverrwite = state?.announcements.find(a => a.productId === product.id)
		setAnnoucementId(announcementToOverrwite?.id);
		setProductId(product?.id);
		setProductImageSource(products.find(p => p.id === announcementToOverrwite?.productId || null)?.imagePath);
		setCurrentProduct(product);
		setShowProductsModal(false);
	}

	function IsAnnouncementUnique(product) {
		// validate the product/brand is not associated with an existing announcement
		return !state?.announcements?.find(a => a.productId === product.id);
	}

	return (
		<>
			<div className='container-xl mt-4'>
				<AnouncementsCard />
			</div>

			{alert && (
				<div className="container-xxl mt-4">
					<div className="row justify-content-center">
						<div className="col-12 col-md-10 col-lg-8">
							<AlertModal alert={alert} />
						</div>
					</div>
				</div>
			)}
			<div className='container-xxl mt-4'>
				<div className="row justify-content-center">
					<div className="col-12 col-md-10 col-lg-8 my-0">
						<PageTitle title={getConfigValue(appState?.config?.AnnouncementManagement?.Title, appState)?.Value || "Announcement Management"} />
					</div>
				</div>
			</div>

			<div className='container-xxl mt-4'>
				<div className='row justify-content-center'>
					<div className='col-12 col-md-10 col-lg-8'>
						<div className='card pd-card py-3 mb-5'>
							<div className='container'>
								<div className='d-flex justify-content-between align-items-center mb-3'>
									<h2 className="type-20 type-bold mb-0">{getConfigValue(appState?.config?.AnnouncementManagement?.Form?.Title, appState)?.Value || "Announcement Banner"}</h2>
								</div>

								<div className="container-xl mt-3 mt-md-4">
									<div className="row d-flex align-items-md-start mb-5">
										<div className="col-12 col-md-4 mb-3 mb-md-0">
											<ProductsModalButton
												setShowProductsModal={setShowProductsModal}
												productImageSource={productImageSource}
												productName={currentProduct?.name}
											></ProductsModalButton>

										</div>
									</div>
								</div>

								<div className="form-check mb-3 mb-md-4">
									<input
										className="form-check-input"
										type="checkbox"
										checked={annoucementsEnabled}
										onChange={handleAnnoucementsEnableChecked}
										id={`annoucements-enabled-checkbox`}
									>
									</input>
									<label className="form-check-label type-14" htmlFor={`annoucements-enabled-checkbox`}>{getConfigValue(appState?.config?.AnnouncementManagement?.Form?.IsActive?.Label, appState)?.Value || "Active"}</label>
								</div>
								<div className="mb-2">
									<label className="form-check-label type-14" htmlFor={`toolbar`}>{getConfigValue(appState?.config?.AnnouncementManagement?.Form?.Content?.Label, appState)?.Value || "Message"}</label>
								</div>
								<TextEditor
									content={content}
									setContent={setContent}
									rawCharacterCount={rawCharacterCount}
									setRawCharacterCount={setRawCharacterCount}
									maxAnnouncementCharacterCount={maxAnnouncementCharacterCount}
									setHasPendingChange={setHasPendingChange}
								/>
							</div>
						</div>
						<div className="row justify-content-center justify-content-md-end">
							<div className="col-12 col-sm-8 col-md-8 col-lg-7 col-xl-6">
								<div className="d-flex flex-column flex-md-row-reverse">
									<button disabled={!hasPendingChange} type="submit" onClick={handleSaveAnnoucementClick} className="btn btn-success btn-lg mb-3 mb-md-0 ms-md-3 flex-fill w-100">{getConfigValue(appState?.config?.AnnouncementManagement?.Form?.Button?.Save?.Label, appState)?.Value || "Save"}</button>
									<Link to="/AnnouncementManagementPage" className="btn btn-secondary btn-lg flex-fill w-100">{getConfigValue(appState?.config?.Common?.Cancel, appState)?.Value || "Cancel"}</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div >

			<BrandsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onBrandClick={onProductClick}
				isValid={IsAnnouncementUnique}
				onOverwriteClick={onOverwriteClick}
				hasPendingChange={hasPendingChange}
            />
		</>
	);
}
